import { CANVAS_CONFIG, getCanvasConfig, getLadderBounds } from './constants.js';

/**
 * Generate constrained horizontal lines that lead to targetIndex from ALL possible starts
 * Uses generate-and-verify approach to ensure correct result
 * @param {Array} verticalLines - Array of vertical line objects {x, columnIndex}
 * @param {number|null} startIndex - Starting column index (null = verify all starts)
 * @param {number} targetIndex - Target destination column index
 * @param {number} totalCount - Total number of horizontal lines to generate
 * @param {Object} gameConfig - Game configuration with canvasHeight
 * @returns {Array} Array of horizontal line objects {x1, x2, y, columnIndex}
 */
export function generateConstrainedHorizontalLines(verticalLines, startIndex, targetIndex, totalCount = 15, gameConfig = null) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const minSpacing = canvasConfig.minHorizontalSpacing;
  const neighborSpacing = canvasConfig.neighborHorizontalSpacing;
  const { rungAreaTop, rungAreaBottom } = getLadderBounds(gameConfig);

  const startY = rungAreaTop;
  const bottomY = rungAreaBottom;
  const usableHeight = bottomY - startY;
  const columnCount = verticalLines.length - 1;
  const playerCount = verticalLines.length;

  // Maximum retry attempts
  const maxRetries = 100;

  for (let retry = 0; retry < maxRetries; retry++) {
    const horizontalLines = [];
    const usedPositions = {};

    // Generate random horizontal lines distributed across all columns
    for (let i = 0; i < totalCount; i++) {
      let attempts = 0;
      const maxAttempts = 50;

      while (attempts < maxAttempts) {
        const columnIndex = Math.floor(Math.random() * columnCount);
        const y = startY + Math.random() * usableHeight;

        // Check spacing
        const hasSpacing =
          (!usedPositions[columnIndex] ||
            !usedPositions[columnIndex].some(pos => Math.abs(pos - y) < minSpacing)) &&
          !horizontalLines.some(line => {
            const isNeighbor = Math.abs(line.columnIndex - columnIndex) === 1;
            return isNeighbor && Math.abs(line.y - y) < neighborSpacing;
          });

        if (hasSpacing) {
          const line1 = verticalLines[columnIndex];
          const line2 = verticalLines[columnIndex + 1];

          horizontalLines.push({
            x1: line1.x,
            x2: line2.x,
            y,
            columnIndex,
          });

          if (!usedPositions[columnIndex]) {
            usedPositions[columnIndex] = [];
          }
          usedPositions[columnIndex].push(y);
          break;
        }

        attempts++;
      }
    }

    // Sort by Y coordinate
    horizontalLines.sort((a, b) => a.y - b.y);

    // VERIFY: Test ALL possible starting positions
    if (startIndex === null) {
      // Must work for ALL starts
      let allValid = true;

      for (let testStart = 0; testStart < playerCount; testStart++) {
        const { resultIndex } = calculatePath(testStart, verticalLines, horizontalLines, gameConfig);

        if (resultIndex !== targetIndex) {
          allValid = false;
          break;
        }
      }

      if (allValid) {
        // Success! All starting positions lead to target
        console.log(`Generated valid ladder on retry ${retry + 1}`);
        return horizontalLines;
      }
    } else {
      // Only verify the specific start index
      const { resultIndex } = calculatePath(startIndex, verticalLines, horizontalLines, gameConfig);

      if (resultIndex === targetIndex) {
        return horizontalLines;
      }
    }

    // Failed verification - try again with different randomness
  }

  // If all retries failed, fall back to a guaranteed method
  console.warn(`Failed to generate valid ladder after ${maxRetries} retries. Using fallback method.`);

  // Fallback: Create a funnel pattern that guides all paths to target
  const horizontalLines = [];
  const segments = Math.floor(usableHeight / 50); // Divide into segments

  for (let seg = 0; seg < segments; seg++) {
    const y = startY + seg * 50 + Math.random() * 30;

    // For each column, decide if it needs a line to guide toward target
    for (let col = 0; col < columnCount; col++) {
      // Calculate which direction this column needs to move
      const leftOfTarget = col < targetIndex;
      const rightOfTarget = col >= targetIndex;

      // Add lines that push columns toward the target
      if (leftOfTarget && Math.random() > 0.5) {
        // Left side: add line to move right
        const line1 = verticalLines[col];
        const line2 = verticalLines[col + 1];

        horizontalLines.push({
          x1: line1.x,
          x2: line2.x,
          y,
          columnIndex: col,
        });
      } else if (rightOfTarget && Math.random() > 0.5 && col < columnCount) {
        // Right side: don't add lines (stay or move left naturally)
        // Skip
      }
    }
  }

  horizontalLines.sort((a, b) => a.y - b.y);
  return horizontalLines;
}

/**
 * Generate horizontal lines for the ladder game (legacy - random generation)
 * @param {Array} verticalLines - Array of vertical line objects {x, columnIndex}
 * @param {number} totalCount - Total number of horizontal lines to generate
 * @param {Object} gameConfig - Game configuration with canvasHeight
 * @returns {Array} Array of horizontal line objects {x1, x2, y, columnIndex}
 */
export function generateHorizontalLines(verticalLines, totalCount = 15, gameConfig = null) {
  const horizontalLines = [];
  const canvasConfig = getCanvasConfig(gameConfig);
  const minSpacing = canvasConfig.minHorizontalSpacing;
  const neighborSpacing = canvasConfig.neighborHorizontalSpacing;
  const { rungAreaTop, rungAreaBottom } = getLadderBounds(gameConfig);

  const startY = rungAreaTop;
  const bottomY = rungAreaBottom;
  const usableHeight = bottomY - startY;

  const columnCount = verticalLines.length - 1; // 세로선 쌍의 개수

  // 각 column에 균등하게 분배 (최소 1개씩)
  const baseCount = Math.floor(totalCount / columnCount);
  const extraCount = totalCount % columnCount;

  // For each adjacent pair of vertical lines
  for (let i = 0; i < columnCount; i++) {
    const line1 = verticalLines[i];
    const line2 = verticalLines[i + 1];

    // 이 column에 생성할 가로선 개수
    const lineCount = baseCount + (i < extraCount ? 1 : 0);
    const positions = [];

    for (let j = 0; j < lineCount; j++) {
      let y;
      let attempts = 0;
      const maxAttempts = 200;

      // Keep trying to find a valid Y position
      do {
        y = startY + Math.random() * usableHeight;
        attempts++;

        // Check if position is valid
        const isValid =
          // 같은 columnIndex 내에서 최소 간격 유지
          !positions.some(pos => Math.abs(pos - y) < minSpacing) &&
          !horizontalLines.some(line => {
            const isNeighbor = Math.abs(line.columnIndex - i) === 1;
            return isNeighbor && Math.abs(line.y - y) < neighborSpacing;
          });

        if (isValid) break;
      } while (attempts < maxAttempts);

      // Only add if we found a valid position
      if (attempts < maxAttempts) {
        positions.push(y);
        horizontalLines.push({
          x1: line1.x,
          x2: line2.x,
          y,
          columnIndex: i,
        });
      }
    }
  }

  // Sort by Y coordinate for proper rendering order
  horizontalLines.sort((a, b) => a.y - b.y);

  return horizontalLines;
}

/**
 * Calculate the path the ball takes through the ladder
 * @param {number} startIndex - Starting column index
 * @param {Array} verticalLines - Array of vertical line objects
 * @param {Array} horizontalLines - Array of horizontal line objects
 * @param {Object} gameConfig - Game configuration with canvasHeight
 * @returns {Object} {path: Array of {x, y} positions, resultIndex: final column index}
 */
export function calculatePath(startIndex, verticalLines, horizontalLines, gameConfig = null) {
  const path = [];
  const { pathTop, pathBottom } = getLadderBounds(gameConfig);
  const step = 1;

  let currentX = verticalLines[startIndex].x;
  let currentY = pathTop;
  let currentColumnIndex = startIndex;
  const bottomY = pathBottom;

  // Track used horizontal lines to prevent re-crossing
  const usedLines = new Set();

  // Add starting position
  path.push({ x: currentX, y: currentY, type: 'start' });

  // Walk down from top to bottom
  while (currentY < bottomY) {
    // Move down 1px
    currentY += step;
    if (currentY > bottomY) currentY = bottomY;

    // Find horizontal line connected to current column (check BOTH directions)
    // - columnIndex === currentColumnIndex means we're on the LEFT side of the line
    // - columnIndex === currentColumnIndex - 1 means we're on the RIGHT side of the line
    const horizontalLine = horizontalLines.find(line => {
      if (usedLines.has(line)) return false; // Skip already used lines
      if (Math.abs(line.y - currentY) >= 2) return false;
      return line.columnIndex === currentColumnIndex ||
             line.columnIndex === currentColumnIndex - 1;
    });

    if (horizontalLine) {
      // Mark this line as used
      usedLines.add(horizontalLine);

      // Determine direction: are we on the left or right side of this line?
      let targetX;
      let newColumnIndex;

      if (horizontalLine.columnIndex === currentColumnIndex) {
        // We're on the LEFT side → move RIGHT (to x2)
        targetX = horizontalLine.x2;
        newColumnIndex = currentColumnIndex + 1;
      } else {
        // We're on the RIGHT side → move LEFT (to x1)
        targetX = horizontalLine.x1;
        newColumnIndex = currentColumnIndex - 1;
      }

      // Set currentY to exact line position
      currentY = horizontalLine.y;

      // Move pixel by pixel across the horizontal line
      const direction = targetX > currentX ? 1 : -1;
      while (currentX !== targetX) {
        currentX += direction * step;
        if (direction > 0 && currentX > targetX) currentX = targetX;
        if (direction < 0 && currentX < targetX) currentX = targetX;
        path.push({ x: currentX, y: currentY, type: 'horizontal' });
      }

      // Update column index
      currentColumnIndex = newColumnIndex;

      // Move past the line to avoid re-crossing
      currentY = horizontalLine.y + step;
      path.push({ x: currentX, y: currentY, type: 'vertical' });
    } else {
      // No horizontal line here, just move down
      path.push({ x: currentX, y: currentY, type: 'vertical' });
    }
  }

  // Make sure we end at exact bottom
  currentY = bottomY;
  if (path[path.length - 1].y !== bottomY) {
    path[path.length - 1] = { x: currentX, y: bottomY, type: 'vertical' };
  }

  // Clamp result index to valid range
  const resultIndex = Math.min(
    Math.max(currentColumnIndex, 0),
    verticalLines.length - 1
  );

  return {
    path,
    resultIndex,
  };
}

/**
 * Initialize game ladder (vertical and horizontal lines)
 * @param {Array} verticalLines - Vertical lines
 * @param {number} totalCount - Total number of horizontal lines
 * @param {Object} gameConfig - Game configuration with canvasHeight
 * @returns {Object} {verticalLines, horizontalLines}
 */
export function initializeLadder(verticalLines, totalCount = 15, gameConfig = null) {
  const horizontalLines = generateHorizontalLines(verticalLines, totalCount, gameConfig);

  return {
    verticalLines,
    horizontalLines,
  };
}
